import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import {
  Building2, Users, Package, Trash2, DollarSign, Clock,
  CheckCircle2, XCircle, Download, Eye, Search, Filter,
  Shield, AlertTriangle, RefreshCw, Loader2, Ban, RotateCcw,
  ChevronDown, ChevronUp, UserCheck, UserX, BarChart3, Upload, FileText, UserPlus,
  Calendar, TrendingUp, TrendingDown, FileSpreadsheet, Wallet, CreditCard, QrCode, Printer, Film,
  Edit3, X, AlertTriangle as AlertTriangleIcon, Save, Trash, Crown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MonthlyPaymentsTab from "./superadmin/MonthlyPaymentsTab";
import { QRCodeSVG } from "qrcode.react";
import { useSearchParams } from "react-router-dom";

const CHART_COLORS = ["#0EA5E9", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

const SuperAdminDashboard = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [dashboard, setDashboard] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountFilter, setAccountFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", password: "", full_name: "", role: "coursier", phone: "", commune: "" });
  const [createLoading, setCreateLoading] = useState(false);
  const [expandedPharmacy, setExpandedPharmacy] = useState(null);
  const [pharmacyData, setPharmacyData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [subStats, setSubStats] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [compSortBy, setCompSortBy] = useState("recovery_rate");
  const [compSortDir, setCompSortDir] = useState("desc");
  const [monthlyPayments, setMonthlyPayments] = useState(null);
  const [mpLoading, setMpLoading] = useState(false);
  const [mpYear, setMpYear] = useState(new Date().getFullYear());
  const [mpMonth, setMpMonth] = useState(new Date().getMonth() + 1);
  const [mpExpandedClient, setMpExpandedClient] = useState(null);
  const [mpDownloading, setMpDownloading] = useState(false);
  const [subRequests, setSubRequests] = useState([]);
  const [subLoading, setSubLoading] = useState(false);

  // Edit / Delete account modals
  const [editingAccount, setEditingAccount] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(null);
  const [deleteHard, setDeleteHard] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteSaving, setDeleteSaving] = useState(false);

  // QR Codes tab
  const [qrMode, setQrMode] = useState("site"); // "site" | "register"
  const [qrDownloading, setQrDownloading] = useState(false);

  // When opening edit modal, pre-fill form
  useEffect(() => {
    if (editingAccount) {
      setEditForm({
        email: editingAccount.email || "",
        full_name: editingAccount.full_name || "",
        pharmacy_name: editingAccount.pharmacy_name || "",
        phone: editingAccount.phone || "",
        whatsapp: editingAccount.whatsapp || "",
        commune: editingAccount.commune || "",
        ville: editingAccount.ville || "",
        role: editingAccount.role || "",
        password: "",
      });
    } else {
      setEditForm({});
    }
  }, [editingAccount]);

  useEffect(() => {
    if (!deletingAccount) {
      setDeleteHard(false);
      setDeleteConfirmText("");
    }
  }, [deletingAccount]);

  // ─── QR Codes config ───
  const SITE_URL = "https://cmes-pharma.org";
  const REGISTER_URL = `${SITE_URL}/register`;
  const VCARD_DATA = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    "FN:Dr KOBOU DIDIER",
    "N:KOBOU;Didier;;Dr;",
    "ORG:CMES - Coaching Médical et Épanouissement Social",
    "TITLE:Pharmacien Directeur Général",
    "ROLE:Consultant & Formateur",
    "TEL;TYPE=CELL,VOICE:+2250708302383",
    "TEL;TYPE=WORK,VOICE:+225 07 08 30 23 83",
    "EMAIL;TYPE=WORK:kobabdid2006@yahoo.fr",
    "EMAIL;TYPE=WORK:innovationcmes@gmail.com",
    "URL:https://cmes-pharma.org",
    "ADR;TYPE=WORK:;;Abidjan;;;;Côte d'Ivoire",
    "NOTE:Pharmacien Directeur Général — Consultant & Formateur. Plateforme de gestion pharmaceutique et logistique d'assurance.",
    "END:VCARD",
  ].join("\n");

  const QR_PRESETS = {
    site: {
      url: SITE_URL,
      title: "Accédez à CMES Pharma",
      subtitle: "Scannez ce code pour accéder à la plateforme",
      filename: "CMES_QR_Acces_Site",
      services_visible: true,
      who_visible: false,
      gradient: "linear-gradient(135deg, #0c4a6e, #0284c7)",
      caption_color: "#bae6fd",
    },
    register: {
      url: REGISTER_URL,
      title: "Inscrivez-vous sur CMES-LOG",
      subtitle: "Scannez ce QR code avec votre téléphone",
      filename: "CMES_QR_Inscription",
      services_visible: true,
      who_visible: true,
      gradient: "linear-gradient(135deg, #064e3b, #059669)",
      caption_color: "#a7f3d0",
    },
    contact: {
      url: VCARD_DATA,
      title: "Dr KOBOU DIDIER",
      subtitle: "Scannez pour ajouter à vos contacts",
      filename: "CMES_QR_Contact_Direct",
      services_visible: false,
      who_visible: false,
      gradient: "linear-gradient(135deg, #4c1d95, #7c3aed)",
      caption_color: "#ddd6fe",
      is_vcard: true,
    },
  };

  const downloadQRSVG = () => {
    const svg = document.querySelector("#qr-print-zone svg");
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const blob = new Blob([svgStr], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${QR_PRESETS[qrMode].filename}.svg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("QR téléchargé en SVG");
  };

  const downloadQRPNG = async () => {
    setQrDownloading(true);
    try {
      const svg = document.querySelector("#qr-print-zone svg");
      if (!svg) return;
      const serializer = new XMLSerializer();
      const svgStr = serializer.serializeToString(svg);
      // High-res PNG: render at 1024px
      const img = new Image();
      const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      await new Promise((resolve, reject) => {
        img.onload = () => {
          const size = 1024;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, size, size);
          ctx.drawImage(img, 0, 0, size, size);
          canvas.toBlob((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = `${QR_PRESETS[qrMode].filename}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
            URL.revokeObjectURL(url);
            resolve();
          }, "image/png");
        };
        img.onerror = reject;
        img.src = url;
      });
      toast.success("QR téléchargé en PNG (1024×1024)");
    } catch (err) {
      toast.error("Erreur lors du téléchargement PNG");
    } finally {
      setQrDownloading(false);
    }
  };

  const downloadQRPDF = async () => {
    setQrDownloading(true);
    try {
      const { jsPDF } = await import("jspdf");
      const el = document.getElementById("qr-print-zone");
      if (!el) return;
      // Use html2canvas to render the card
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/png");
      // A4 portrait
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = 210;
      const pageH = 297;
      const imgW = 150;
      const imgH = (canvas.height * imgW) / canvas.width;
      const x = (pageW - imgW) / 2;
      const y = (pageH - imgH) / 2;
      pdf.addImage(imgData, "PNG", x, y, imgW, imgH);
      pdf.save(`${QR_PRESETS[qrMode].filename}.pdf`);
      toast.success("QR téléchargé en PDF");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors du téléchargement PDF");
    } finally {
      setQrDownloading(false);
    }
  };

  const submitEditAccount = async () => {
    if (!editingAccount) return;
    setEditSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      const res = await axios.put(
        `${API}/superadmin/accounts/${editingAccount.id}`,
        payload,
        { headers }
      );
      toast.success(res.data?.message || "Compte mis à jour");
      setEditingAccount(null);
      loadAccounts();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setEditSaving(false);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!deletingAccount) return;
    const expected = (deletingAccount.email || "").toLowerCase();
    if (deleteConfirmText.trim().toLowerCase() !== expected) {
      toast.error("Veuillez saisir exactement l'email du compte pour confirmer");
      return;
    }
    setDeleteSaving(true);
    try {
      const res = await axios.delete(
        `${API}/superadmin/accounts/${deletingAccount.id}`,
        { headers, params: { hard: deleteHard } }
      );
      toast.success(res.data?.message || "Compte supprimé");
      setDeletingAccount(null);
      loadAccounts();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleteSaving(false);
    }
  };

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadDashboard();
    loadAccounts();
    loadAnalytics();
    loadComparison();
    loadSubRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/dashboard`, { headers });
      setDashboard(res.data);
    } catch (err) {
      toast.error("Erreur de chargement du tableau de bord");
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/accounts`, { headers });
      setAccounts(res.data);
    } catch (err) {
      toast.error("Erreur de chargement des comptes");
    }
  };

  const loadAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/analytics/superadmin/charts`, { headers });
      setAnalytics(res.data);
    } catch (err) {
    }
  };

  const loadComparison = async () => {
    try {
      const res = await axios.get(`${API}/analytics/superadmin/pharmacy-comparison`, { headers });
      setComparison(res.data);
    } catch (err) {
    }
  };

  const loadSubRequests = async () => {
    try {
      const [reqRes, subRes] = await Promise.all([
        axios.get(`${API}/subscription-plans/pending-requests`, { headers }),
        axios.get(`${API}/subscription-plans/subscribers`, { headers }),
      ]);
      setSubRequests(reqRes.data.requests || []);
      setSubscribers(subRes.data.subscribers || []);
      setSubStats(subRes.data.stats || null);
    } catch (err) {
    }
  };

  const handleSubAction = async (userId, action) => {
    setActionLoading(userId);
    try {
      await axios.post(`${API}/subscription-plans/${action}/${userId}`, {}, { headers });
      toast.success(action === "activate" ? "Abonnement activé !" : "Demande refusée");
      loadSubRequests();
      loadAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setActionLoading(null);
    }
  };


  const loadMonthlyPayments = async (y, m) => {
    setMpLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/superadmin/monthly-payments`, {
        headers, params: { year: y || mpYear, month: m || mpMonth }
      });
      setMonthlyPayments(res.data);
    } catch (err) {
      toast.error("Erreur de chargement du suivi des paiements");
    } finally { setMpLoading(false); }
  };

  const downloadPaymentReport = async (format) => {
    setMpDownloading(true);
    try {
      const res = await axios.get(`${API}/analytics/superadmin/monthly-payments/download`, {
        headers, params: { year: mpYear, month: mpMonth, format },
        responseType: "blob"
      });
      const ext = format === "csv" ? "csv" : "pdf";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `CMES_Suivi_Paiements_${mpYear}-${String(mpMonth).padStart(2,"0")}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Rapport ${ext.toUpperCase()} téléchargé`);
    } catch (err) {
      toast.error("Erreur de téléchargement");
    } finally { setMpDownloading(false); }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setImportLoading(true);
    try {
      const res = await axios.post(`${API}/superadmin/import-destructions`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      setImportResult(res.data);
      toast.success(`${res.data.imported_count} destructions importées`);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    } finally {
      setImportLoading(false);
      e.target.value = "";
    }
  };

  const handleAccountAction = async (userId, action) => {
    setActionLoading(`${userId}-${action}`);
    try {
      await axios.post(`${API}/superadmin/accounts/${userId}/${action}`, {}, { headers });
      const labels = { approve: "approuvé", reject: "rejeté", suspend: "suspendu", reactivate: "réactivé" };
      toast.success(`Compte ${labels[action]} avec succès`);
      loadAccounts();
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'action");
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportData = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/export-data`, {
        headers,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `cmes_export_${new Date().toISOString().split("T")[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Données exportées avec succès");
    } catch (err) {
      toast.error("Erreur d'exportation");
    }
  };

  const viewPharmacyData = async (userId) => {
    if (expandedPharmacy === userId) {
      setExpandedPharmacy(null);
      setPharmacyData(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/superadmin/pharmacy/${userId}/data`, { headers });
      setPharmacyData(res.data);
      setExpandedPharmacy(userId);
    } catch (err) {
      toast.error("Erreur de chargement des données");
    }
  };

  const filteredAccounts = accounts.filter(a => {
    const matchFilter = accountFilter === "all" || a.status === accountFilter;
    const matchRole = roleFilter === "all" || a.role === roleFilter;
    const matchSearch = !searchTerm ||
      (a.full_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.pharmacy_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.email || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchFilter && matchSearch && matchRole;
  });

  const pendingCount = accounts.filter(a => a.status === "pending").length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="superadmin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-white flex items-center gap-2">
              <Shield className="w-7 h-7 text-amber-500" />
              Super Administration
            </h1>
            <p className="text-slate-400 text-sm mt-1">Vue d'ensemble et gestion globale de la plateforme</p>
          </div>
          <a href="/log" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors" data-testid="back-to-log">
            <BarChart3 className="w-4 h-4" /> Retour CMES-LOG
          </a>
          <div className="flex gap-2">
            <Button onClick={handleExportData} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="export-data-btn">
              <Download className="w-4 h-4 mr-2" />
              Exporter les données
            </Button>
            <Button onClick={() => { loadDashboard(); loadAccounts(); loadAnalytics(); loadComparison(); }} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Link to="/superadmin/analytics-vitrine" data-testid="link-vitrine-analytics">
              <Button variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics Vitrine
              </Button>
            </Link>
            <Link to="/superadmin/training-videos" data-testid="link-training-videos">
              <Button variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
                <Film className="w-4 h-4 mr-2" />
                Vidéos commerciales
              </Button>
            </Link>
            <Link to="/superadmin/gestion" data-testid="link-internal-finance">
              <Button className="bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-700 hover:to-violet-700 text-white">
                <Wallet className="w-4 h-4 mr-2" />
                Gestion Interne
              </Button>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          {[
            { id: "overview", label: "Vue d'ensemble", icon: Building2 },
            { id: "subscriptions", label: `Abonnements${subRequests.length > 0 ? ` (${subRequests.length})` : ""}`, icon: CreditCard },
            { id: "payments", label: "Suivi Paiements", icon: Wallet },
            { id: "comparison", label: "Comparatif", icon: BarChart3 },
            { id: "analytics", label: "Analytique", icon: BarChart3 },
            { id: "import", label: "Import en masse", icon: Upload },
            { id: "accounts", label: `Comptes${pendingCount > 0 ? ` (${pendingCount})` : ""}`, icon: Users },
            { id: "qr-inscription", label: "QR Codes", icon: QrCode },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all
                ${activeTab === tab.id
                  ? "bg-cyan-500/10 text-cyan-600 border-b-2 border-cyan-500"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === "accounts" && pendingCount > 0 && (
                <span className="bg-red-500/100 text-white text-xs rounded-full px-2 py-0.5 ml-1">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && dashboard && (
          <div className="space-y-6">
            {/* Quick-access Product Cards (UX refonte) */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {[
                { to: "/superadmin/gestion", label: "Gestion Interne", desc: "Recouvrements, dépenses, trésorerie", icon: Wallet, gradient: "from-fuchsia-500 to-violet-600" },
                { to: "/superadmin?tab=accounts", label: "Comptes", desc: "Utilisateurs & rôles", icon: Users, gradient: "from-cyan-500 to-blue-600" },
                { to: "/superadmin?tab=subscriptions", label: "Abonnements", desc: "Plans & renouvellements", icon: Crown, gradient: "from-amber-500 to-orange-600" },
                { to: "/superadmin?tab=payments", label: "Paiements mensuels", desc: "Recouvrement pharmacies", icon: CreditCard, gradient: "from-emerald-500 to-teal-600" },
                { to: "/superadmin?tab=insurers", label: "Assureurs", desc: "Base référentielle CI", icon: Shield, gradient: "from-blue-500 to-indigo-600" },
                { to: "/superadmin?tab=qr-inscription", label: "QR Codes", desc: "Accès site / Inscription / Contact", icon: QrCode, gradient: "from-violet-500 to-purple-600" },
                { to: "/superadmin/training-videos", label: "Vidéos", desc: "Studio commercial IA", icon: Film, gradient: "from-rose-500 to-red-600" },
                { to: "/superadmin/analytics-vitrine", label: "Analytics Vitrine", desc: "Visiteurs & conversions", icon: BarChart3, gradient: "from-pink-500 to-fuchsia-600" },
              ].map((c) => {
                const I = c.icon;
                return (
                  <Link key={c.to} to={c.to} data-testid={`quick-${c.label.toLowerCase().replace(/ /g, "-")}`}>
                    <div className={`group relative overflow-hidden bg-gradient-to-br ${c.gradient} rounded-xl p-4 h-full hover:scale-105 transition-transform cursor-pointer`}>
                      <I className="w-6 h-6 text-white/90 mb-2" />
                      <div className="text-sm font-bold text-white">{c.label}</div>
                      <div className="text-[10px] text-white/70 mt-1">{c.desc}</div>
                      <div className="absolute -bottom-4 -right-4 opacity-20 group-hover:opacity-30 transition-opacity">
                        <I className="w-20 h-20 text-white" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Pharmacies actives", value: dashboard.overview.active_pharmacies, icon: Building2, color: "cyan", sub: `${dashboard.overview.pending_pharmacies} en attente` },
                { label: "Total produits", value: dashboard.overview.total_products, icon: Package, color: "blue" },
                { label: "Destructions", value: dashboard.overview.total_destructions, icon: Trash2, color: "amber", sub: `${dashboard.overview.pending_destructions} en attente` },
                { label: "Valeur financière", value: `${(dashboard.overview.total_financial_value || 0).toLocaleString()} FCFA`, icon: DollarSign, color: "green" },
              ].map((kpi, i) => (
                <div key={kpi.label} className="bg-slate-900 border border-white/10 rounded-xl p-4" data-testid={`kpi-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <kpi.icon className={`w-5 h-5 text-${kpi.color}-400`} />
                    <span className={`text-xs font-medium text-${kpi.color}-400 bg-${kpi.color}-500/10 px-2 py-0.5 rounded-full`}>
                      {kpi.label}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-white">{kpi.value}</p>
                  {kpi.sub && <p className="text-xs text-slate-500 mt-1">{kpi.sub}</p>}
                </div>
              ))}
            </div>

            {/* By Commune + Recent */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900 border border-white/10 rounded-xl">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-lg font-semibold text-white">Destructions par commune</h3>
                </div>
                <div className="p-4">
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {(dashboard.by_commune || []).map((c, i) => (
                      <div key={c.commune || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <span className="text-sm text-slate-300 font-medium">{c.commune}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs bg-cyan-500/100/10 text-cyan-400 px-2 py-0.5 rounded-full">{c.count} dest.</span>
                          <span className="text-xs text-slate-500">{(c.value || 0).toLocaleString()} F</span>
                        </div>
                      </div>
                    ))}
                    {(!dashboard.by_commune || dashboard.by_commune.length === 0) && (
                      <p className="text-sm text-slate-500 text-center py-4">Aucune donnée</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-slate-900 border border-white/10 rounded-xl">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-lg font-semibold text-white">Inscriptions récentes</h3>
                </div>
                <div className="p-4">
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {(dashboard.recent_users || []).map((u, i) => (
                      <div key={u.email || u.id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                        <div>
                          <p className="text-sm text-slate-300 font-medium">{u.pharmacy_name || u.full_name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                          ${u.status === "active" ? "bg-green-500/100/10 text-green-400" : ""}
                          ${u.status === "pending" ? "bg-amber-500/100/10 text-amber-400" : ""}
                          ${u.status === "rejected" ? "bg-red-500/100/10 text-red-400" : ""}
                          ${u.status === "suspended" ? "bg-white/50/10 text-slate-400" : ""}
                        `}>
                          {u.status === "active" ? "Actif" : u.status === "pending" ? "En attente" : u.status === "rejected" ? "Rejeté" : "Suspendu"}
                        </span>
                      </div>
                    ))}
                    {(!dashboard.recent_users || dashboard.recent_users.length === 0) && (
                      <p className="text-sm text-slate-500 text-center py-4">Aucune inscription</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Subscriptions Tab */}
        {activeTab === "subscriptions" && (
          <div className="space-y-6" data-testid="subscriptions-tab">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-sky-600" />
                Demandes d'abonnement
              </h2>
              <Button onClick={loadSubRequests} variant="outline" size="sm" className="text-slate-600 border-slate-200 text-xs gap-1">
                <RefreshCw className="w-3 h-3" /> Actualiser
              </Button>
            </div>

            {subRequests.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-slate-700 font-medium">Aucune demande en attente</p>
                <p className="text-xs text-slate-500 mt-1">Toutes les demandes d'abonnement ont été traitées.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subRequests.map((req) => (
                  <div key={req.id} className="bg-white border border-amber-200 rounded-xl p-5 shadow-sm" data-testid={`sub-request-${req.user_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${req.trial_expired ? "bg-red-100 text-red-700" : req.status === "trial" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                            {req.trial_expired ? "Essai expire" : req.status === "trial" ? `Essai (${req.trial_days_remaining ?? "?"}j restants)` : "En attente"}
                          </span>
                          {req.type === "plan_change" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold">Changement</span>}
                          <span className="text-xs text-slate-400">{new Date(req.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        <h3 className="text-base font-bold text-slate-900">{req.pharmacy_name || req.full_name || "Pharmacie"}</h3>
                        <p className="text-sm text-slate-500">{req.email}</p>
                        <div className="mt-2 flex items-center gap-4">
                          <div className="flex items-center gap-1">
                            <CreditCard className="w-3.5 h-3.5 text-sky-500" />
                            <span className="text-sm font-medium text-slate-700">{req.plan_name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                            <span className="text-sm font-bold text-emerald-600">{new Intl.NumberFormat("fr-FR").format(req.price)} FCFA/mois</span>
                          </div>
                          {req.module && (
                            <span className="text-[10px] bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">{req.module}</span>
                          )}
                        </div>
                        {req.previous_plan && (
                          <p className="text-[10px] text-slate-400 mt-1">Ancien plan : {req.previous_plan}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleSubAction(req.user_id, "activate")}
                          disabled={actionLoading === req.user_id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1"
                          data-testid={`activate-sub-${req.user_id}`}
                        >
                          {actionLoading === req.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          Confirmer paiement
                        </Button>
                        <Button
                          onClick={() => handleSubAction(req.user_id, "reject")}
                          disabled={actionLoading === req.user_id}
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50 text-xs gap-1"
                          data-testid={`reject-sub-${req.user_id}`}
                        >
                          <XCircle className="w-3 h-3" /> Refuser
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Subscription Stats */}
            {subStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-white border border-emerald-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">Abonnes payes</p>
                  <p className="text-2xl font-bold text-emerald-600">{subStats.active_paid}</p>
                </div>
                <div className="bg-white border border-sky-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">En essai gratuit</p>
                  <p className="text-2xl font-bold text-sky-600">{subStats.active_trial}</p>
                </div>
                <div className="bg-white border border-red-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">Essais expires</p>
                  <p className="text-2xl font-bold text-red-500">{subStats.trial_expired}</p>
                </div>
                <div className="bg-white border border-amber-200 rounded-xl p-4">
                  <p className="text-xs text-slate-500 mb-1">Revenu mensuel</p>
                  <p className="text-2xl font-bold text-amber-600">{new Intl.NumberFormat("fr-FR").format(subStats.monthly_revenue)} F</p>
                </div>
              </div>
            )}

            {/* Subscribers List */}
            {subscribers.length > 0 && (
              <div className="mt-6">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-emerald-600" />
                  Abonnes actifs ({subscribers.length})
                </h2>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Pharmacie</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Formule</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Tarif</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Statut</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((sub) => (
                        <tr key={sub.user_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`subscriber-${sub.user_id}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900 text-sm">{sub.pharmacy_name || sub.full_name}</p>
                            <p className="text-xs text-slate-400">{sub.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-slate-700">{sub.plan_name}</span>
                            {sub.plan_module && <p className="text-[10px] text-slate-400">{sub.plan_module}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-bold text-slate-700">{new Intl.NumberFormat("fr-FR").format(sub.plan_price)} F/mois</span>
                          </td>
                          <td className="px-4 py-3">
                            {sub.is_trial && !sub.trial_expired && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold">Essai ({sub.trial_days_remaining}j)</span>
                            )}
                            {sub.trial_expired && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Essai expire</span>
                            )}
                            {!sub.is_trial && sub.subscription_status === "active" && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Paye</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {(sub.is_trial || sub.trial_expired) && (
                              <Button onClick={() => handleSubAction(sub.user_id, "activate")} disabled={actionLoading === sub.user_id}
                                size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1" data-testid={`activate-subscriber-${sub.user_id}`}>
                                {actionLoading === sub.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                Confirmer paiement
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Monthly Payments Tab */}
        {activeTab === "payments" && (
          <MonthlyPaymentsTab
            data={monthlyPayments}
            loading={mpLoading}
            year={mpYear}
            month={mpMonth}
            onYearChange={setMpYear}
            onMonthChange={setMpMonth}
            onLoad={loadMonthlyPayments}
            onDownload={downloadPaymentReport}
            downloading={mpDownloading}
            expandedClient={mpExpandedClient}
            onToggleClient={setMpExpandedClient}
          />
        )}


        {/* Comparison Tab */}
        {activeTab === "comparison" && comparison && (
          <div className="space-y-6" data-testid="comparison-tab">
            {/* Global KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: "Pharmacies", value: comparison.global_totals.total_pharmacies, color: "cyan" },
                { label: "Total factures", value: comparison.global_totals.total_invoices, color: "blue" },
                { label: "Montant global", value: `${(comparison.global_totals.total_amount / 1000000).toFixed(1)}M`, color: "white" },
                { label: "Total recouvré", value: `${(comparison.global_totals.total_paid / 1000000).toFixed(1)}M`, color: "emerald" },
                { label: "Taux moyen", value: `${comparison.global_totals.avg_recovery_rate}%`, color: "amber" },
              ].map((kpi, i) => (
                <div key={kpi.label} className="bg-slate-900 border border-white/10 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-slate-500 uppercase">{kpi.label}</p>
                  <p className={`text-xl font-bold text-${kpi.color}-400`}>{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* Sort controls */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Trier par :</span>
              {[
                { key: "recovery_rate", label: "Taux de recouvrement" },
                { key: "total_amount", label: "Montant total" },
                { key: "total_invoices", label: "Nb factures" },
                { key: "total_remaining", label: "Reste à payer" },
              ].map(s => (
                <button key={s.key} onClick={() => { setCompSortBy(s.key); setCompSortDir(prev => compSortBy === s.key ? (prev === "desc" ? "asc" : "desc") : "desc"); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${compSortBy === s.key ? "bg-cyan-600 text-white" : "bg-slate-900 text-slate-400 border border-white/10 hover:bg-slate-800"}`}>
                  {s.label} {compSortBy === s.key && (compSortDir === "desc" ? "↓" : "↑")}
                </button>
              ))}
            </div>

            {/* Ranking Table */}
            <div className="bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="comparison-table">
                  <thead>
                    <tr className="text-xs text-slate-500 uppercase border-b border-white/5 bg-slate-950">
                      <th className="text-left px-4 py-3">#</th>
                      <th className="text-left px-4 py-3">Pharmacie</th>
                      <th className="text-left px-4 py-3">Commune</th>
                      <th className="text-center px-4 py-3">Factures</th>
                      <th className="text-right px-4 py-3">Montant total</th>
                      <th className="text-right px-4 py-3">Payé</th>
                      <th className="text-right px-4 py-3">Reste</th>
                      <th className="text-center px-4 py-3">Taux recouvrement</th>
                      <th className="text-center px-4 py-3">Objectif</th>
                      <th className="text-center px-4 py-3">Produits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...comparison.pharmacies]
                      .sort((a, b) => compSortDir === "desc" ? b[compSortBy] - a[compSortBy] : a[compSortBy] - b[compSortBy])
                      .map((p, i) => (
                      <tr key={p.pharmacy_id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`comp-row-${i}`}>
                        <td className="px-4 py-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            i === 0 ? "bg-amber-500 text-slate-900" : i === 1 ? "bg-slate-400 text-slate-900" : i === 2 ? "bg-amber-700 text-white" : "bg-slate-800 text-slate-400"
                          }`}>{i + 1}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{p.pharmacy_name}</p>
                          <p className="text-[10px] text-slate-600">{p.ville}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{p.commune}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-white font-medium">{p.total_invoices}</span>
                          <div className="flex justify-center gap-1 mt-0.5">
                            {p.paid_count > 0 && <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-1 rounded">{p.paid_count} payé</span>}
                            {p.partial_count > 0 && <span className="text-[10px] bg-orange-500/10 text-orange-400 px-1 rounded">{p.partial_count} partiel</span>}
                            {p.pending_count > 0 && <span className="text-[10px] bg-blue-500/10 text-blue-400 px-1 rounded">{p.pending_count} att.</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-white font-medium">{(p.total_amount || 0).toLocaleString("fr-FR")} F</td>
                        <td className="px-4 py-3 text-right text-emerald-400 font-medium">{(p.total_paid || 0).toLocaleString("fr-FR")} F</td>
                        <td className="px-4 py-3 text-right text-amber-400 font-medium">{(p.total_remaining || 0).toLocaleString("fr-FR")} F</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-slate-800 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${p.recovery_rate >= (p.target_rate || 80) ? "bg-emerald-500" : p.recovery_rate >= (p.target_rate || 80) * 0.8 ? "bg-amber-500" : "bg-red-500"}`}
                                style={{ width: `${Math.min(p.recovery_rate, 100)}%` }} />
                            </div>
                            <span className={`text-xs font-bold ${p.recovery_rate >= (p.target_rate || 80) ? "text-emerald-400" : p.recovery_rate >= (p.target_rate || 80) * 0.8 ? "text-amber-400" : "text-red-400"}`}>
                              {p.recovery_rate}%
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-xs text-slate-500">{p.target_rate || 80}%</span>
                          {p.gap != null && (
                            <span className={`ml-1 text-[10px] font-bold ${p.gap >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              ({p.gap >= 0 ? "+" : ""}{p.gap})
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-slate-400 text-xs">{p.products_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {comparison.pharmacies.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune pharmacie avec des factures</p>
                </div>
              )}
            </div>

            {/* Recovery Rate Distribution Chart */}
            {comparison.pharmacies.length > 0 && (
              <div className="bg-slate-900 border border-white/10 rounded-xl">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-lg font-semibold text-white">Taux de recouvrement par pharmacie</h3>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparison.pharmacies.slice(0, 15)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis type="number" domain={[0, 100]} stroke="#64748b" fontSize={11} tickFormatter={v => `${v}%`} />
                      <YAxis dataKey="pharmacy_name" type="category" width={140} stroke="#64748b" fontSize={10} />
                      <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }}
                        formatter={(v) => [`${v}%`, "Taux"]} />
                      <Bar dataKey="recovery_rate" name="Taux de recouvrement" radius={[0, 4, 4, 0]}>
                        {comparison.pharmacies.slice(0, 15).map((p, i) => (
                          <Cell key={i} fill={p.recovery_rate >= 80 ? "#10B981" : p.recovery_rate >= 50 ? "#F59E0B" : "#EF4444"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Analytics Tab */}
        {activeTab === "analytics" && analytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Destruction Trends */}
              <div className="bg-slate-900 border border-white/10 rounded-xl" data-testid="chart-destruction-trend">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-lg font-semibold text-white">Tendance des destructions</h3>
                </div>
                <div className="p-4">
                  {analytics.destruction_trend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={analytics.destruction_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                        <YAxis yAxisId="left" stroke="#0EA5E9" fontSize={11} />
                        <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" fontSize={11} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                        <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }} />
                        <Legend wrapperStyle={{ color: "#94a3b8" }} />
                        <Area yAxisId="left" type="monotone" dataKey="count" name="Nombre" stroke="#0EA5E9" fill="#0EA5E9" fillOpacity={0.15} strokeWidth={2} />
                        <Area yAxisId="right" type="monotone" dataKey="value" name="Valeur (FCFA)" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.1} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">Aucune donnée disponible</p>
                  )}
                </div>
              </div>

              {/* Commune Distribution */}
              <div className="bg-slate-900 border border-white/10 rounded-xl" data-testid="chart-commune-dist">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-lg font-semibold text-white">Répartition par commune</h3>
                </div>
                <div className="p-4">
                  {analytics.commune_distribution?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={analytics.commune_distribution} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis type="number" stroke="#64748b" fontSize={11} />
                        <YAxis dataKey="commune" type="category" width={90} stroke="#64748b" fontSize={11} />
                        <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }} />
                        <Legend wrapperStyle={{ color: "#94a3b8" }} />
                        <Bar dataKey="destructions" name="Destructions" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="products" name="Produits" fill="#10B981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">Aucune donnée disponible</p>
                  )}
                </div>
              </div>

              {/* Category Distribution */}
              <div className="bg-slate-900 border border-white/10 rounded-xl" data-testid="chart-category-dist">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-lg font-semibold text-white">Produits par catégorie</h3>
                </div>
                <div className="p-4">
                  {analytics.category_distribution?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie data={analytics.category_distribution} dataKey="count" nameKey="category" cx="50%" cy="50%"
                          outerRadius={100} label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={{ stroke: "#94a3b8" }} fontSize={11}>
                          {analytics.category_distribution.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [`${v} produits`, n]} contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">Aucune donnée disponible</p>
                  )}
                </div>
              </div>

              {/* Ville Distribution */}
              <div className="bg-slate-900 border border-white/10 rounded-xl" data-testid="chart-ville-dist">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-lg font-semibold text-white">Pharmacies par ville</h3>
                </div>
                <div className="p-4">
                  {analytics.ville_distribution?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={analytics.ville_distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="ville" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#1e293b" }} />
                        <Bar dataKey="count" name="Pharmacies" fill="#0EA5E9" radius={[4, 4, 0, 0]}>
                          {analytics.ville_distribution.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">Aucune donnée disponible</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Import Tab */}
        {activeTab === "import" && (
          <div className="space-y-6">
            <div className="bg-slate-900 border border-white/10 rounded-xl" data-testid="bulk-import-card">
              <div className="p-4 border-b border-white/5">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Upload className="w-5 h-5 text-cyan-400" />
                  Import en masse — Destructions historiques
                </h3>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-cyan-500/100/5 border border-cyan-500/20 rounded-lg p-4">
                  <p className="text-sm text-cyan-400 font-medium mb-2">Format CSV attendu :</p>
                  <code className="text-xs text-cyan-300 block bg-slate-950 rounded p-3">
                    pharmacie,commune,année,produit,quantite,prix,valeur_totale,lot{"\n"}
                    Pharmacie Cocody,Cocody,2023,Paracétamol 500mg,200,500,100000,LOT-2023-001
                  </code>
                  <p className="text-xs text-cyan-500 mt-2">Formats acceptés : CSV (.csv), Excel (.xlsx) et PDF (.pdf) avec tableaux structurés</p>
                </div>
                <label className={`flex flex-col items-center justify-center gap-3 px-6 py-8 border-2 border-dashed rounded-xl cursor-pointer transition-all
                  ${importLoading ? "border-slate-700 bg-slate-950" : "border-cyan-500/30 bg-slate-950 hover:bg-slate-900 hover:border-cyan-400/50"}`}>
                  {importLoading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
                  ) : (
                    <Upload className="w-8 h-8 text-cyan-400" />
                  )}
                  <span className="text-sm font-medium text-cyan-400">
                    {importLoading ? "Import en cours..." : "Glissez ou cliquez pour importer"}
                  </span>
                  <input type="file" accept=".csv,.pdf,.xlsx,.xls" onChange={handleBulkImport} className="hidden" disabled={importLoading} data-testid="bulk-import-input" />
                </label>
                {importResult && (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4" data-testid="import-result">
                    <p className="font-semibold text-emerald-400">{importResult.imported_count} destructions importées</p>
                    {importResult.errors_count > 0 && (
                      <p className="text-sm text-amber-400 mt-1">{importResult.errors_count} erreur(s)</p>
                    )}
                    {importResult.imported.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500 border-b border-white/5"><th className="text-left py-1">Pharmacie</th><th className="text-left py-1">Année</th></tr>
                          </thead>
                          <tbody>
                            {importResult.imported.map((item, i) => (
                              <tr key={item.pharmacy + '-' + item.year + '-' + i} className="border-b border-white/5">
                                <td className="py-1 text-slate-300">{item.pharmacy}</td>
                                <td className="py-1 text-slate-500">{item.year}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* API Documentation */}
            <div className="bg-slate-900 border border-white/10 rounded-xl">
              <div className="p-4 border-b border-white/5">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-400" />
                  API pour logiciels tiers
                </h3>
              </div>
              <div className="p-4">
                <div className="bg-slate-950 rounded-lg p-4 text-sm font-mono">
                  <p className="text-green-400 mb-2"># Import de produits via API</p>
                  <p className="text-slate-300">POST /api/external/products?api_key=votre_cle</p>
                  <p className="text-slate-500 text-xs mt-1">Fichier CSV avec: name, batch_number, expiry_date, quantity, unit_price</p>
                  <p className="text-green-400 mt-4 mb-2"># Import de données analytiques</p>
                  <p className="text-slate-300">POST /api/external/analytics?api_key=votre_cle</p>
                  <p className="text-slate-500 text-xs mt-1">JSON: year, annual_order_value, unsold_value, expired_stock_value...</p>
                  <p className="text-green-400 mt-4 mb-2"># Générer une clé API</p>
                  <p className="text-slate-300">Chaque pharmacie peut générer sa clé via son espace personnel</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === "accounts" && (
          <div className="space-y-4">
            {/* Create Account Button */}
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-white">Gestion des comptes</h2>
              <button onClick={() => setShowCreateAccount(!showCreateAccount)} data-testid="btn-create-account"
                className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                <UserPlus className="w-4 h-4" />
                Créer un compte
              </button>
            </div>

            {/* Create Account Form */}
            {showCreateAccount && (
              <div className="bg-slate-900 border border-cyan-500/20 rounded-xl p-4 space-y-3" data-testid="create-account-form">
                <h3 className="font-semibold text-cyan-400 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Nouveau compte Agent Commercial ou Admin</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <select value={createForm.role} onChange={e => setCreateForm(p => ({ ...p, role: e.target.value }))} data-testid="create-role"
                    className="bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                    <option value="coursier">Agent Commercial</option>
                    <option value="admin">Administrateur</option>
                  </select>
                  <Input value={createForm.full_name} onChange={e => setCreateForm(p => ({ ...p, full_name: e.target.value }))}
                    placeholder="Nom complet" className="bg-slate-950 border-white/10 text-white" data-testid="create-name" />
                  <Input value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="Email" type="email" className="bg-slate-950 border-white/10 text-white" data-testid="create-email" />
                  <Input value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Mot de passe" type="password" className="bg-slate-950 border-white/10 text-white" data-testid="create-password" />
                  <Input value={createForm.phone} onChange={e => setCreateForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder="Téléphone" className="bg-slate-950 border-white/10 text-white" data-testid="create-phone" />
                  <Input value={createForm.commune} onChange={e => setCreateForm(p => ({ ...p, commune: e.target.value }))}
                    placeholder="Commune" className="bg-slate-950 border-white/10 text-white" data-testid="create-commune" />
                </div>
                <div className="flex gap-2">
                  <button onClick={async () => {
                    setCreateLoading(true);
                    try {
                      await axios.post(`${API}/superadmin/create-account`, createForm, { headers });
                      toast.success(`Compte ${createForm.role} créé avec succès !`);
                      setShowCreateAccount(false);
                      setCreateForm({ email: "", password: "", full_name: "", role: "coursier", phone: "", commune: "" });
                      loadAccounts();
                    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                    finally { setCreateLoading(false); }
                  }} disabled={createLoading} data-testid="submit-create-account"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
                    {createLoading ? "Création..." : "Créer le compte"}
                  </button>
                  <button onClick={() => setShowCreateAccount(false)} className="bg-slate-800 border border-white/10 text-slate-300 px-4 py-2 rounded-lg text-sm">
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Rechercher par nom, pharmacie ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-900 border-white/10 text-white"
                  data-testid="search-accounts"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: "all", label: "Tous" },
                  { id: "pharmacy", label: "Pharmacies" },
                  { id: "etablissement", label: "Établissements" },
                  { id: "coursier", label: "Agents Commerciaux" },
                  { id: "admin", label: "Admins" },
                  { id: "assurance", label: "Assurances" },
                ].map(f => (
                  <button key={f.id} onClick={() => setRoleFilter(f.id)} data-testid={`role-filter-${f.id}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      roleFilter === f.id ? "bg-blue-600 text-white" : "bg-slate-900 text-slate-400 border border-white/10 hover:bg-slate-800"
                    }`}>
                    {f.label}
                    {f.id !== "all" && <span className="ml-1">({accounts.filter(a => a.role === f.id).length})</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              {[
                { id: "all", label: "Tous" },
                { id: "pending", label: "En attente", color: "amber" },
                { id: "active", label: "Actifs", color: "green" },
                { id: "suspended", label: "Suspendus", color: "slate" },
                { id: "rejected", label: "Rejetés", color: "red" },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setAccountFilter(f.id)}
                  data-testid={`filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                    ${accountFilter === f.id
                      ? "bg-cyan-600 text-white"
                      : "bg-slate-900 text-slate-400 border border-white/10 hover:bg-slate-800"
                    }`}
                >
                  {f.label}
                  {f.id !== "all" && (
                    <span className="ml-1 text-xs">({accounts.filter(a => a.status === f.id).length})</span>
                  )}
                </button>
              ))}
            </div>

            {/* Accounts List */}
            <div className="space-y-3">
              {filteredAccounts.map((account) => (
                <div key={account.id} className="bg-slate-900 border border-white/10 rounded-xl p-4" data-testid={`account-${account.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-cyan-400" />
                          <h3 className="font-semibold text-white">{account.full_name || account.pharmacy_name || "N/A"}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            (account.role === "coursier" || account.role === "agent commercial") ? "bg-indigo-500/10 text-indigo-400" :
                            account.role === "admin" ? "bg-amber-500/100/10 text-amber-400" :
                            account.role === "assurance" ? "bg-teal-500/10 text-teal-400" :
                            account.role === "etablissement" ? "bg-emerald-500/10 text-emerald-400" :
                            "bg-cyan-500/100/10 text-cyan-400"
                          }`}>{account.role === "etablissement" ? "Établissement" : (account.role === "coursier" || account.role === "agent commercial") ? "Agent Commercial" : account.role}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                            ${account.status === "active" ? "bg-green-500/100/10 text-green-400" : ""}
                            ${account.status === "pending" ? "bg-amber-500/100/10 text-amber-400 animate-pulse" : ""}
                            ${account.status === "rejected" ? "bg-red-500/100/10 text-red-400" : ""}
                            ${account.status === "suspended" ? "bg-white/50/10 text-slate-400" : ""}
                          `}>
                            {account.status === "active" ? "Actif" : account.status === "pending" ? "En attente" : account.status === "rejected" ? "Rejeté" : "Suspendu"}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{account.full_name} - {account.email}</p>
                        <div className="flex gap-4 mt-1 text-xs text-slate-500">
                          {account.establishment_type && <span className="text-emerald-400 font-medium">{account.establishment_type.replace(/_/g, ' ')}</span>}
                          {account.ville && <span>Ville: {account.ville}</span>}
                          {account.commune && <span>Commune: {account.commune}</span>}
                          {account.phone && <span>Tél: {account.phone}</span>}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {account.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleAccountAction(account.id, "approve")}
                              disabled={actionLoading === `${account.id}-approve`}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-${account.id}`}
                            >
                              {actionLoading === `${account.id}-approve` ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3 mr-1" />}
                              Approuver
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAccountAction(account.id, "reject")}
                              disabled={actionLoading === `${account.id}-reject`}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/100/100/10"
                              data-testid={`reject-${account.id}`}
                            >
                              {actionLoading === `${account.id}-reject` ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserX className="w-3 h-3 mr-1" />}
                              Rejeter
                            </Button>
                          </>
                        )}
                        {account.status === "active" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAccountAction(account.id, "suspend")}
                            disabled={actionLoading === `${account.id}-suspend`}
                            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/100/100/10"
                            data-testid={`suspend-${account.id}`}
                          >
                            {actionLoading === `${account.id}-suspend` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
                            Suspendre
                          </Button>
                        )}
                        {(account.status === "suspended" || account.status === "rejected") && (
                          <Button
                            size="sm"
                            onClick={() => handleAccountAction(account.id, "reactivate")}
                            disabled={actionLoading === `${account.id}-reactivate`}
                            className="bg-cyan-600 hover:bg-cyan-700 text-white"
                            data-testid={`reactivate-${account.id}`}
                          >
                            {actionLoading === `${account.id}-reactivate` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                            Réactiver
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => viewPharmacyData(account.id)}
                          className="text-cyan-400 hover:bg-cyan-500/100/100/10"
                          data-testid={`view-${account.id}`}
                        >
                          {expandedPharmacy === account.id ? <ChevronUp className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                          {expandedPharmacy === account.id ? "Masquer" : "Voir détails"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingAccount(account)}
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          data-testid={`edit-${account.id}`}
                          title="Modifier les informations (email, mot de passe, nom...)"
                        >
                          <Edit3 className="w-3 h-3 mr-1" />
                          Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeletingAccount(account)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-${account.id}`}
                          title="Supprimer le compte (libère l'email pour réutilisation)"
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Supprimer
                        </Button>
                      </div>
                    </div>

                    {/* Expanded pharmacy data */}
                    {expandedPharmacy === account.id && pharmacyData && (
                      <div className="mt-4 pt-4 border-t border-white/5">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div className="bg-cyan-500/100/5 border border-cyan-500/20 rounded-lg p-3">
                            <p className="text-xs text-cyan-400">Produits</p>
                            <p className="text-lg font-bold text-white">{pharmacyData.stats.total_products}</p>
                          </div>
                          <div className="bg-amber-500/100/5 border border-amber-500/20 rounded-lg p-3">
                            <p className="text-xs text-amber-400">Destructions</p>
                            <p className="text-lg font-bold text-white">{pharmacyData.stats.total_destructions}</p>
                          </div>
                          <div className="bg-blue-500/100/5 border border-blue-500/20 rounded-lg p-3">
                            <p className="text-xs text-blue-400">Valeur stock</p>
                            <p className="text-lg font-bold text-white">{(pharmacyData.stats.total_value || 0).toLocaleString()} F</p>
                          </div>
                          <div className="bg-red-500/100/5 border border-red-500/20 rounded-lg p-3">
                            <p className="text-xs text-red-400">Valeur détruite</p>
                            <p className="text-lg font-bold text-white">{(pharmacyData.stats.destruction_value || 0).toLocaleString()} F</p>
                          </div>
                        </div>
                        {pharmacyData.products.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold text-slate-400 mb-2">Derniers produits :</p>
                            <div className="max-h-40 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-500 border-b border-white/5">
                                    <th className="text-left py-1">Nom</th>
                                    <th className="text-left py-1">Catégorie</th>
                                    <th className="text-right py-1">Qté</th>
                                    <th className="text-right py-1">Statut</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pharmacyData.products.slice(0, 10).map((p, i) => (
                                    <tr key={p.name + '-' + i} className="border-b border-white/5">
                                      <td className="py-1 text-slate-300">{p.name}</td>
                                      <td className="py-1 text-slate-500">{p.category}</td>
                                      <td className="py-1 text-right text-slate-300">{p.quantity}</td>
                                      <td className="py-1 text-right">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${p.status === "active" ? "bg-green-500/100/10 text-green-400" : "bg-amber-500/100/10 text-amber-400"}`}>
                                          {p.status}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              ))}

              {filteredAccounts.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Aucun compte trouvé</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* QR Codes Tab (Inscription + Accès au site) */}
        {activeTab === "qr-inscription" && (
          <div className="space-y-6" data-testid="qr-inscription-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-semibold text-slate-800 text-lg">QR Codes CMES</h2>
                <p className="text-sm text-slate-500 mt-1">Téléchargez ou imprimez les QR codes pour vos courriers, cartes de visite et affiches.</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={downloadQRPNG}
                  disabled={qrDownloading}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                  data-testid="qr-download-png-btn"
                >
                  {qrDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  PNG
                </Button>
                <Button
                  onClick={downloadQRSVG}
                  disabled={qrDownloading}
                  variant="outline"
                  className="border-slate-300 text-slate-700 gap-2"
                  data-testid="qr-download-svg-btn"
                >
                  <Download className="w-4 h-4" /> SVG
                </Button>
                <Button
                  onClick={downloadQRPDF}
                  disabled={qrDownloading}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 gap-2"
                  data-testid="qr-download-pdf-btn"
                >
                  {qrDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  PDF
                </Button>
                <Button onClick={() => {
                  const el = document.getElementById("qr-print-zone");
                  if (el) {
                    const w = window.open("", "_blank");
                    w.document.write(`<html><head><title>CMES - QR Code</title><style>@page{margin:0}body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#fff;font-family:system-ui,-apple-system,sans-serif}</style></head><body>${el.outerHTML}</body></html>`);
                    w.document.close();
                    setTimeout(() => { w.print(); }, 500);
                  }
                }} className="bg-slate-800 hover:bg-slate-900 text-white gap-2" data-testid="print-qr-btn">
                  <Printer className="w-4 h-4" /> Imprimer
                </Button>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="flex justify-center">
              <div className="inline-flex bg-slate-100 rounded-xl p-1 border border-slate-200 flex-wrap">
                <button
                  onClick={() => setQrMode("site")}
                  data-testid="qr-mode-site"
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${qrMode === "site" ? "bg-white shadow text-cyan-700" : "text-slate-600 hover:text-slate-800"}`}
                >
                  Accès au site
                </button>
                <button
                  onClick={() => setQrMode("register")}
                  data-testid="qr-mode-register"
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${qrMode === "register" ? "bg-white shadow text-emerald-700" : "text-slate-600 hover:text-slate-800"}`}
                >
                  Inscription pharmacie
                </button>
                <button
                  onClick={() => setQrMode("contact")}
                  data-testid="qr-mode-contact"
                  className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${qrMode === "contact" ? "bg-white shadow text-violet-700" : "text-slate-600 hover:text-slate-800"}`}
                >
                  Contact direct
                </button>
              </div>
            </div>

            {/* Printable QR Card */}
            <div className="flex justify-center">
              <div id="qr-print-zone" style={{ width: 480, background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}>
                {/* Header */}
                <div style={{ background: QR_PRESETS[qrMode].gradient, padding: "28px 32px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#fff", letterSpacing: -0.5 }}>CMES</div>
                  <div style={{ fontSize: 11, color: QR_PRESETS[qrMode].caption_color, marginTop: 2, letterSpacing: 1.5, textTransform: "uppercase" }}>Coaching Médical et Épanouissement Social</div>
                </div>

                {/* QR Section */}
                <div style={{ padding: "32px 32px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", marginBottom: 4 }}>{QR_PRESETS[qrMode].title}</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 24 }}>{QR_PRESETS[qrMode].subtitle}</div>

                  <div style={{ display: "inline-block", padding: 16, background: "#f8fafc", borderRadius: 12, border: "2px solid #e2e8f0" }}>
                    <QRCodeSVG
                      value={QR_PRESETS[qrMode].url}
                      size={220}
                      level="H"
                      includeMargin={false}
                      fgColor="#0f172a"
                      bgColor="#f8fafc"
                      imageSettings={{
                        src: "https://customer-assets.emergentagent.com/job_0d053155-ce00-4580-8f42-e450395a24b2/artifacts/1forsbvk_logo%20cmes.png",
                        height: 44,
                        width: 44,
                        excavate: true,
                      }}
                    />
                  </div>

                  {!QR_PRESETS[qrMode].is_vcard && (
                    <div style={{ marginTop: 16, fontSize: 11, color: "#94a3b8" }}>
                      {QR_PRESETS[qrMode].url}
                    </div>
                  )}
                  {QR_PRESETS[qrMode].is_vcard && (
                    <div style={{ marginTop: 16, fontSize: 11, color: "#94a3b8" }}>
                      Compatible iOS / Android — ajoute automatiquement le contact
                    </div>
                  )}
                </div>

                {/* Contact info block (vCard mode only) */}
                {QR_PRESETS[qrMode].is_vcard && (
                  <div style={{ padding: "0 32px 20px" }}>
                    <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "14px 16px", border: "1px solid #ddd6fe" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#5b21b6", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Pharmacien Directeur Général</div>
                      <div style={{ fontSize: 10, color: "#7c3aed", fontStyle: "italic", marginBottom: 10 }}>Consultant & Formateur</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#334155" }}>
                        <div><span style={{ color: "#7c3aed", fontWeight: 600 }}>Téléphone:</span> +225 07 08 30 23 83</div>
                        <div><span style={{ color: "#7c3aed", fontWeight: 600 }}>Email 1:</span> kobabdid2006@yahoo.fr</div>
                        <div><span style={{ color: "#7c3aed", fontWeight: 600 }}>Email 2:</span> innovationcmes@gmail.com</div>
                        <div><span style={{ color: "#7c3aed", fontWeight: 600 }}>Site:</span> cmes-pharma.org</div>
                        <div><span style={{ color: "#7c3aed", fontWeight: 600 }}>Adresse:</span> Abidjan, Côte d'Ivoire</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Services */}
                {QR_PRESETS[qrMode].services_visible && (
                  <div style={{ padding: "0 32px 20px" }}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                      {[
                        { label: "CMES-LOG", desc: "Logistique & Facturation", color: "#0284c7", bg: "#f0f9ff" },
                        { label: "INTEL-STOCK", desc: "Stocks & Périmés", color: "#059669", bg: "#f0fdf4" },
                        { label: "TECH-CONSEIL", desc: "IA Pharmaceutique", color: "#7c3aed", bg: "#f5f3ff" },
                      ].map(s => (
                        <div key={s.label} style={{ flex: 1, background: s.bg, borderRadius: 8, padding: "10px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: s.color }}>{s.label}</div>
                          <div style={{ fontSize: 8, color: "#64748b", marginTop: 2 }}>{s.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Who (only on register QR) */}
                {QR_PRESETS[qrMode].who_visible && (
                  <div style={{ padding: "0 32px 20px", textAlign: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 6 }}>Ouvert aux :</div>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                      {["Pharmacies d'officine", "Hôpitaux & Cliniques", "Centres de santé", "Laboratoires"].map(t => (
                        <span key={t} style={{ fontSize: 9, background: "#f1f5f9", color: "#475569", padding: "4px 10px", borderRadius: 20, fontWeight: 500 }}>{t}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", padding: "14px 32px", textAlign: "center" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#334155" }}>Dr KOBOU DIDIER — Pharmacien Directeur Général</div>
                  <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>kobabdid2006@yahoo.fr | innovationcmes@gmail.com | +225 07 08 30 23 83</div>
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>Abidjan, Côte d'Ivoire</div>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-center">
                <FileText className="w-6 h-6 text-sky-600 mx-auto mb-2" />
                <p className="text-xs font-semibold text-sky-800">Dans vos courriers</p>
                <p className="text-[10px] text-sky-600 mt-1">Insérez le PNG ou PDF dans vos en-têtes de lettres, devis et factures</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <Printer className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-xs font-semibold text-emerald-800">Imprimé sur carte</p>
                <p className="text-[10px] text-emerald-600 mt-1">Distribuez lors de vos rendez-vous ou événements professionnels</p>
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-center">
                <QrCode className="w-6 h-6 text-violet-600 mx-auto mb-2" />
                <p className="text-xs font-semibold text-violet-800">Sur votre site web</p>
                <p className="text-[10px] text-violet-600 mt-1">Utilisez le SVG (vectoriel, qualité parfaite à toute taille) pour vos pages web</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============ EDIT ACCOUNT MODAL ============ */}
      {editingAccount && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !editSaving && setEditingAccount(null)}
          data-testid="edit-account-modal"
        >
          <div
            className="bg-slate-900 border border-blue-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-5 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-blue-400" />
                  Modifier le compte
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  {editingAccount.email} — {editingAccount.role}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingAccount(null)}
                disabled={editSaving}
                data-testid="edit-account-close-btn"
              >
                <X className="w-5 h-5 text-slate-400" />
              </Button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">Email *</label>
                  <Input
                    type="email"
                    value={editForm.email || ""}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                    data-testid="edit-account-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">Nom complet</label>
                  <Input
                    value={editForm.full_name || ""}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                    data-testid="edit-account-fullname"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">Nom de la pharmacie</label>
                  <Input
                    value={editForm.pharmacy_name || ""}
                    onChange={(e) => setEditForm({ ...editForm, pharmacy_name: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                    data-testid="edit-account-pharmacy"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">Téléphone</label>
                  <Input
                    value={editForm.phone || ""}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                    data-testid="edit-account-phone"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">WhatsApp</label>
                  <Input
                    value={editForm.whatsapp || ""}
                    onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                    data-testid="edit-account-whatsapp"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">Commune</label>
                  <Input
                    value={editForm.commune || ""}
                    onChange={(e) => setEditForm({ ...editForm, commune: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                    data-testid="edit-account-commune"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">Ville</label>
                  <Input
                    value={editForm.ville || ""}
                    onChange={(e) => setEditForm({ ...editForm, ville: e.target.value })}
                    className="bg-slate-800 border-white/10 text-white"
                    data-testid="edit-account-ville"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 mb-1 block">Rôle</label>
                  <select
                    value={editForm.role || ""}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded-md text-white px-3 py-2 text-sm"
                    data-testid="edit-account-role"
                  >
                    <option value="pharmacy">Pharmacie</option>
                    <option value="coursier">Agent Commercial (coursier)</option>
                    <option value="admin">Admin</option>
                    <option value="assurance">Assurance</option>
                    <option value="etablissement">Établissement</option>
                  </select>
                </div>
              </div>

              <div className="pt-3 border-t border-white/10">
                <label className="text-xs font-semibold text-amber-400 mb-1 block">
                  Nouveau mot de passe (laisser vide pour ne pas changer)
                </label>
                <Input
                  type="text"
                  value={editForm.password || ""}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="6 caractères minimum"
                  className="bg-slate-800 border-amber-500/30 text-white"
                  data-testid="edit-account-password"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Le nouveau mot de passe sera communiqué manuellement au titulaire du compte.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
                <Button
                  variant="outline"
                  onClick={() => setEditingAccount(null)}
                  disabled={editSaving}
                  className="border-white/10 text-slate-300"
                  data-testid="edit-account-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={submitEditAccount}
                  disabled={editSaving || !editForm.email}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="edit-account-save-btn"
                >
                  {editSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ DELETE ACCOUNT CONFIRM MODAL ============ */}
      {deletingAccount && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleteSaving && setDeletingAccount(null)}
          data-testid="delete-account-modal"
        >
          <div
            className="bg-slate-900 border border-red-500/30 rounded-2xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-white/10 p-5 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <AlertTriangleIcon className="w-5 h-5 text-red-400" />
                Supprimer le compte
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeletingAccount(null)}
                disabled={deleteSaving}
                data-testid="delete-account-close-btn"
              >
                <X className="w-5 h-5 text-slate-400" />
              </Button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-sm text-red-300 font-semibold">
                  {deletingAccount.email}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {deletingAccount.full_name || deletingAccount.pharmacy_name || ""} — {deletingAccount.role}
                </p>
              </div>

              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                  <input
                    type="radio"
                    checked={!deleteHard}
                    onChange={() => setDeleteHard(false)}
                    className="mt-1"
                    data-testid="delete-mode-soft"
                  />
                  <div>
                    <p className="text-sm font-semibold text-white">Suppression simple (recommandée)</p>
                    <p className="text-xs text-slate-400">
                      Libère l'email pour réutilisation. Conserve l'historique (factures, courses) pour l'audit.
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-red-500/30 cursor-pointer hover:bg-red-500/5">
                  <input
                    type="radio"
                    checked={deleteHard}
                    onChange={() => setDeleteHard(true)}
                    className="mt-1"
                    data-testid="delete-mode-hard"
                  />
                  <div>
                    <p className="text-sm font-semibold text-red-300">Suppression définitive (cascade)</p>
                    <p className="text-xs text-slate-400">
                      Supprime aussi factures, courses, documents et notifications. Action irréversible.
                    </p>
                  </div>
                </label>
              </div>

              <div className="pt-2">
                <label className="text-xs font-semibold text-slate-400 mb-1 block">
                  Pour confirmer, saisissez l'email exact du compte :
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deletingAccount.email}
                  className="bg-slate-800 border-red-500/30 text-white"
                  data-testid="delete-confirm-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
                <Button
                  variant="outline"
                  onClick={() => setDeletingAccount(null)}
                  disabled={deleteSaving}
                  className="border-white/10 text-slate-300"
                  data-testid="delete-account-cancel-btn"
                >
                  Annuler
                </Button>
                <Button
                  onClick={confirmDeleteAccount}
                  disabled={deleteSaving || deleteConfirmText.trim().toLowerCase() !== (deletingAccount.email || "").toLowerCase()}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-account-confirm-btn"
                >
                  {deleteSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Trash className="w-4 h-4 mr-2" />
                  )}
                  {deleteHard ? "Supprimer définitivement" : "Supprimer (libérer l'email)"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};


export default SuperAdminDashboard;
